import React, { useState } from 'react';
import {
    Typography, Button, Container, Divider, Box, Modal, TextField, CircularProgress,
    Alert, Avatar, Chip, Paper, Stack, MenuItem
} from '@mui/material';
import { CheckCircle, MapPin, XCircle, Calendar, DownloadIcon, Eye, Edit, Globe, RefreshCw, MessageCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useInvitationDetails } from './useInvitationDetails';
import CalendarModal from './Calendar/calendarModal';
import { Wallet, Map } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { truncate } from 'fs';
import axios from 'axios';
import { eventCalendarSpan } from '@/utils/eventTime';
import { GuestDownloadBox, GuestPartnerOffers, GuestSlot, guestLanguage, useGuestPage, useJustConfirmed } from './guestPage/useGuestPage';

const Invitation = () => {
    const params = useParams();
    const { t, i18n } = useTranslation();
    const {
        eventData, eventQrcode, isLoading, isError, errorMessage,
        actionLoading, actionError, setActionError, setActionLoading,
        handleResponse, handleDecline, qrcodeLoading
    } = useInvitationDetails(params.id);

    // Set Arabic as default for invite page
    React.useEffect(() => {
        // Force Arabic for invite page
        i18n.changeLanguage('ar');
        // Also set the HTML dir attribute for RTL
        document.documentElement.setAttribute('dir', 'rtl');
    }, [i18n]);

    const [modalOpen, setModalOpen] = useState(false);
    const [eventImageModalOpen, setEventImageModalOpen] = useState(false);
    const [qrPreviewOpen, setQrPreviewOpen] = useState(false);

    // Modal state
    const [declineModalOpen, setDeclineModalOpen] = useState(false);
    const [declineMessage, setDeclineMessage] = useState('');
    const [goingModalOpen, setGoingModalOpen] = useState(false);
    const [goingCount, setGoingCount] = useState(1);
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageToHost, setMessageToHost] = useState('');

    // Add state for QR code preview modal and original QR blob
    const [qrBlobUrl, setQrBlobUrl] = useState<string | null>(null);

    // Additional guests dialog state
    const [additionalGuestsModalOpen, setAdditionalGuestsModalOpen] = useState(false);
    const [selectedGuestCount, setSelectedGuestCount] = useState<number | null>(null);
    const [customGuestCount, setCustomGuestCount] = useState<string>("");
    const [guestCountError, setGuestCountError] = useState<string>("");


    const eventObj = eventData?.data?.event?.data?.[0];

    // The "Download the Mazoom app" box, partner offers and the guest's actions, each switched on our
    // server (settings list, guest_page). Off or unreachable: nothing changes on this page.
    const guestPage = useGuestPage(eventObj?.event_id, i18n.language);
    const justConfirmed = useJustConfirmed(eventData?.data?.status);

    // Calculate total seats reserved (1 main guest + quantity_members additional guests)
    const totalSeatsReserved = 1 + (eventData?.data?.quantity_members || 0);

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        const locale = i18n.language === 'ar' ? 'ar' : 'en';
        return new Intl.DateTimeFormat(locale, {
            timeZone: 'UTC',
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).format(new Date(dateString));
    };

    const handleGoingClick = () => {
        if ((eventData?.data?.quantity_members || 0) > 0) {
            setAdditionalGuestsModalOpen(true);
        } else {
            handleResponse(1);
        }
    };
    const formatDateCalander = (iso: any) => {
        if (!iso) return 'N/A';
        const locale = i18n.language === 'ar' ? 'ar' : 'en';
        return new Date(iso).toLocaleString(locale);
    };

    // Build a robust maps URL that accepts direct links (maps.app.goo.gl or full Google Maps links),
    // coordinates, or a plain address string
    const buildMapsUrl = (venue?: string | null, addressText?: string | null, lat?: number | null, lng?: number | null) => {
        // Prefer coordinates when available
        if (typeof lat === 'number' && typeof lng === 'number' && isFinite(lat) && isFinite(lng) && (lat !== 0 || lng !== 0)) {
            return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        }

        // Combine and sanitize any pasted link
        const combinedRaw = `${venue || ''} ${addressText || ''}`.trim();
        const combined = combinedRaw.replace(/^\s*["'\(]+|["'\)]+\s*$/g, '');

        // Try to extract a direct link (allow missing scheme, and strip trailing quotes/parentheses)
        const directUrlRegex = /(https?:\/\/)?(?:maps\.app\.goo\.gl|goo\.gl\/maps|www\.google\.com\/maps|maps\.google\.com)[^\s\)"]+/i;
        const direct = combined.match(directUrlRegex)?.[0];
        if (direct) {
            const normalized = direct.startsWith('http') ? direct : `https://${direct}`;
            return normalized;
        }

        // Fallback to a Maps search query using the combined string
        if (combined) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(combined)}`;
        }

        return '';
    };

    const handleConfirmGuests = () => {
        const maxGuests = totalSeatsReserved;
        const finalCount = selectedGuestCount || parseInt(customGuestCount, 10);

        if (!finalCount || isNaN(finalCount)) {
            setGuestCountError("Please select a valid number.");
            return;
        }

        if (finalCount > maxGuests) {
            setGuestCountError(`Maximum allowed is ${maxGuests} people.`);
            return;
        }

        if (finalCount <= 0) {
            setGuestCountError("Number must be greater than 0.");
            return;
        }

        setGuestCountError("");
        // Send only additional guests count (total - 1 main person)
        const additionalGuests = finalCount - 1;
        handleResponse(1, additionalGuests);
        setAdditionalGuestsModalOpen(false);
        setSelectedGuestCount(null);
        setCustomGuestCount("");
    };

    const handleSendMessage = async () => {
        if (!messageToHost.trim()) {
            return;
        }

        setActionLoading(true);
        setActionError(null);
        try {
            const eventObj = eventData?.data?.event?.data?.[0];
            if (eventData?.data?.contact?.id && eventObj?.event_id) {
                await axios.post(`${import.meta.env.VITE_REACT_APP_API_BASE_URL}/events/message`, {
                    message: messageToHost,
                    sender: eventData.data.contact.id,
                    event: eventObj.event_id,
                });

                // Success - clear text, close dialog, and show success banner
                setMessageToHost('');
                setMessageModalOpen(false);
                setActionError(null);

                // Show success banner
                const successBanner = document.createElement('div');
                successBanner.style.cssText = `
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: #4caf50;
                    color: white;
                    padding: 12px 20px;
                    border-radius: 4px;
                    z-index: 9999;
                    font-family: Arial, sans-serif;
                    font-size: 14px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                `;
                successBanner.textContent = 'Message sent successfully!';
                document.body.appendChild(successBanner);

                // Remove banner after 3 seconds
                setTimeout(() => {
                    if (document.body.contains(successBanner)) {
                        document.body.removeChild(successBanner);
                    }
                }, 3000);
            }
        } catch (error: any) {
            setActionError('Message failed to send');
        } finally {
            setActionLoading(false);
        }
    };

    // Real moments in time for the calendar apps: start_datetime's digits are
    // Riyadh time (its "Z" is not real UTC). Events have no end time, so the
    // entry runs a few hours (due_date is only the event's day, at midnight).
    const calendarSpan = eventCalendarSpan(eventObj?.start_datetime);
    const event = {
        title: eventObj?.name || 'N/A',
        description: eventObj?.host_name || 'N/A',
        start: calendarSpan?.start || 'N/A',
        end: calendarSpan?.end || 'N/A',
        location: eventObj?.venue || eventObj?.address?.address || 'N/A',
        address: eventObj?.address?.address || 'N/A',
        lat: eventObj?.address?.lat || null,
        lng: eventObj?.address?.lng || null,
        host: eventObj?.host_name || 'N/A',
        password: eventObj?.password || null,
        language: eventObj?.language || 'en',
        isReminderActive: eventObj?.is_reminder_active || false,
        type: eventObj?.type || null,
        pricePaid: eventObj?.price_paid || 0,
        patternImage: eventObj?.pattern || null, // could be used as event banner
    };

    // Wallet functionality - only triggered on button click
    const [walletLoading, setWalletLoading] = useState(false);
    const [walletError, setWalletError] = useState<string | null>(null);
    const [appleWalletUrl, setAppleWalletUrl] = useState<string | null>(null);

    const handleAddToWallet = async () => {
        if (!eventObj?.event_id || !eventData?.data?.contact?.id) {
            setWalletError('Missing event or contact info');
            return;
        }

        setWalletLoading(true);
        setWalletError(null);

        try {
            const BASE = import.meta.env.VITE_REACT_APP_API_BASE_URL;
            const res = await fetch(`${BASE}/public/events/${eventObj.event_id}/wallet-pass`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer mazoom_guest_page_link`,
                },
                body: JSON.stringify({
                    contact_id: eventData.data.contact.id,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }),
            });

            const data = await res.json();

            if (data.success && data.wallet_url) {
                setAppleWalletUrl(data.wallet_url);

                // Better mobile browser compatibility
                if (isMobileDevice()) {
                    // For mobile devices, use direct navigation
                    window.location.href = data.wallet_url;
                } else {
                    // For desktop, try window.open first, fallback to direct navigation
                    const walletWindow = window.open(data.wallet_url, '_blank');
                    if (!walletWindow || walletWindow.closed || typeof walletWindow.closed === 'undefined') {
                        // Popup was blocked, use direct navigation
                        window.location.href = data.wallet_url;
                    }
                }
            } else {
                setWalletError(data.message || 'Failed to get wallet URL');
            }
        } catch (error) {
            setWalletError('Network error or server unreachable');
        } finally {
            setWalletLoading(false);
        }
    };

    // When eventQrcode changes, create a blob URL for preview/download
    React.useEffect(() => {
        if (eventQrcode && window.atob) {
            // eventQrcode is base64 string, convert to blob for download/preview
            const byteString = window.atob(eventQrcode);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }
            const blob = new Blob([ab], { type: 'image/png' });
            const url = URL.createObjectURL(blob);
            setQrBlobUrl(url);

            // Cleanup old blob URLs
            return () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setQrBlobUrl(null);
        }
    }, [eventQrcode]);

    // Detect if user is on mobile device (Android or iPhone)
    const isMobileDevice = () => {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    };

    // Handle download for different devices
    const handleDownload = () => {
        if (qrBlobUrl) {
            // Generate filename with event name
            const eventName = eventObj?.name || 'Event';
            const fileName = `Invitation-QR-${eventName.replace(/[^a-zA-Z0-9]/g, '-')}.png`;

            // Try Web Share API first (for mobile devices)
            if (navigator.share && isMobileDevice()) {
                fetch(qrBlobUrl)
                    .then(res => res.blob())
                    .then(blob => {
                        const file = new File([blob], fileName, { type: 'image/png' });
                        navigator.share({
                            title: 'Event QR Code',
                            text: 'Save this QR code to your photo gallery',
                            files: [file]
                        }).catch(() => {
                            // Fallback to regular download if share fails
                            downloadQRCode(fileName);
                        });
                    });
            } else {
                // For desktop or if Web Share API fails, use regular download
                downloadQRCode(fileName);
            }
        }
    };

    // Regular download function
    const downloadQRCode = (fileName: string) => {
        if (qrBlobUrl) {
            const link = document.createElement('a');
            link.href = qrBlobUrl;
            link.download = fileName;
            link.click();
        }
    };

    // Language switcher function
    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
        // Update HTML dir attribute based on language
        document.documentElement.setAttribute('dir', newLang === 'ar' ? 'rtl' : 'ltr');
    };

    if (isLoading) {
        return (
            <Container maxWidth="sm" sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', minHeight: '80vh'
            }}>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 3,
                    alignItems: 'center'
                }}>
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: '#000000',
                                animation: 'dotPulse 1.4s ease-in-out infinite',
                                animationDelay: `${index * 0.2}s`,
                                opacity: 0.3
                            }}
                        />
                    ))}
                </Box>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        color: '#666666',
                        letterSpacing: '0.5px'
                    }}
                >
                    {t('invitation.loadingInvitation', 'Loading your invitation...')}
                </Typography>
                <style>
                    {`
                        @keyframes dotPulse {
                            0%, 80%, 100% {
                                transform: scale(0.8);
                                opacity: 0.3;
                            }
                            40% {
                                transform: scale(1);
                                opacity: 1;
                            }
                        }
                    `}
                </style>
            </Container>
        );
    }

    if (errorMessage) {
        return (
            <Container maxWidth="sm" sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                p: 3,
                backgroundColor: '#f5f5f5'
            }}>
                <Paper elevation={0} sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    maxWidth: 500,
                    width: '100%',
                    backgroundColor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                    <Box sx={{ mb: 3 }}>
                        <XCircle size={48} color="#e53935" strokeWidth={1.5} />
                    </Box>
                    <Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
                        تم حذف هذه الدعوه
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        يرجى التواصل مع الداعي
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        startIcon={<RefreshCw />}
                        fullWidth
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 'bold',
                            fontSize: 16,
                            backgroundColor: 'black',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#222'
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: i18n.language === 'ar' ? 0 : 1,
                                marginLeft: i18n.language === 'ar' ? 1 : 0
                            }
                        }}
                    >
                        {t('buttons.tryAgain')}
                    </Button>
                </Paper>
            </Container>
        );
    }
    return (
        <>
            <style>
                {`
                    @keyframes dotPulse {
                        0%, 80%, 100% {
                            transform: scale(0.8);
                            opacity: 0.3;
                        }
                        40% {
                            transform: scale(1);
                            opacity: 1;
                        }
                    }
                `}
            </style>
            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    p: 0,
                    px: 0,
                    width: '100vw',
                    minHeight: '100vh',
                    borderRadius: 0,
                    mx: 0,
                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                    // Center content on large screens (md and up)
                    display: { xs: 'block', md: 'flex' },
                    alignItems: { md: 'center' },
                    justifyContent: { md: 'center' },
                    // Remove maxWidth restriction for tablets (sm, md)
                    '@media (min-width:600px) and (max-width:1279.95px)': {
                        maxWidth: '100vw',
                        minWidth: '100vw',
                    },
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        mt: 0,
                        mx: 0,
                        borderRadius: 0,
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 1,
                        p: 0,
                        width: { xs: '100%', sm: '100%', md: 700 },
                        minHeight: '100vh',
                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                        // Remove border radius for tablets
                        '@media (min-width:600px) and (max-width:1279.95px)': {
                            borderRadius: 0,
                            width: '100%',
                            minHeight: '100vh',
                        },
                    }}
                >
                    {/* Language Switcher Button */}
                    <Box sx={{
                        position: 'absolute',
                        top: 16,
                        [i18n.language === 'ar' ? 'left' : 'right']: 16,
                        zIndex: 10
                    }}>
                        <Button
                            onClick={toggleLanguage}
                            variant="outlined"
                            size="small"
                            sx={{
                                backgroundColor: 'white',
                                color: 'text.primary',
                                borderColor: 'rgba(0, 0, 0, 0.2)',
                                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                '& .lang-button-content': {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: i18n.language === 'ar' ? '8px' : '6px', // Slightly more space for Arabic
                                    position: 'relative',
                                    [i18n.language === 'ar' ? 'right' : 'left']: '-4px'
                                },
                                '&:hover': {
                                    backgroundColor: 'white',
                                    borderColor: 'rgba(0, 0, 0, 0.4)'
                                },
                                minWidth: 'auto',
                                px: 2
                            }}
                        >
                            <span className="lang-button-content">
                                <Globe size={16} />
                                <span>{i18n.language === 'en' ? 'EN' : 'عربي'}</span>
                            </span>
                        </Button>
                    </Box>
                    {eventObj?.pattern && eventObj?.name && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: { xs: '50vh', sm: 242, md: 286 },
                                mb: 0,
                                cursor: 'pointer',
                                borderRadius: 0,
                                overflow: 'hidden',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                animation: 'imageGlow 3s ease-in-out infinite alternate',
                                '@keyframes imageGlow': {
                                    '0%': {
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 0 0 0 rgba(25,118,210,0)',
                                        transform: 'scale(1)'
                                    },
                                    '50%': {
                                        boxShadow: '0 8px 25px rgba(0,0,0,0.2), 0 0 20px 2px rgba(25,118,210,0.1)',
                                    },
                                    '100%': {
                                        boxShadow: '0 12px 35px rgba(0,0,0,0.25), 0 0 30px 4px rgba(25,118,210,0.15)',
                                        transform: 'scale(1.005)'
                                    }
                                },
                                '&:hover': {
                                    animation: 'none',
                                    transform: 'scale(1.02) !important',
                                    boxShadow: '0 15px 40px rgba(0,0,0,0.3), 0 0 40px 6px rgba(25,118,210,0.2)',
                                    transition: 'all 0.3s ease-in-out'
                                }
                            }}
                            onClick={() => setEventImageModalOpen(true)}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(45deg, rgba(25,118,210,0.1) 0%, transparent 30%, transparent 70%, rgba(25,118,210,0.1) 100%)',
                                    animation: 'shimmer 4s ease-in-out infinite',
                                    '@keyframes shimmer': {
                                        '0%': { opacity: 0.3, transform: 'translateX(-100%)' },
                                        '50%': { opacity: 0.8, transform: 'translateX(0%)' },
                                        '100%': { opacity: 0.3, transform: 'translateX(100%)' }
                                    },
                                    zIndex: 1,
                                    pointerEvents: 'none'
                                }}
                            />
                            <img
                                src={eventObj.pattern}
                                alt="Event Banner"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    filter: 'brightness(0.75) contrast(1.1)',
                                    transition: 'all 0.3s ease-in-out'
                                }}
                            />
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 3,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                                pointerEvents: 'none',
                                textAlign: i18n.language === 'ar' ? 'right' : 'left'
                            }}>
                                <Typography variant="h3" component="h1" sx={{
                                    color: 'common.white', fontWeight: 700,
                                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                    mb: 0.5,
                                    textAlign: i18n.language === 'ar' ? 'right' : 'left'
                                }}>
                                    {eventObj.name}
                                </Typography>
                                {eventObj?.host_name && (
                                    <Typography variant="h6" sx={{
                                        color: 'rgba(255, 255, 255, 0.9)', fontWeight: 400,
                                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                        fontSize: '1.1rem',
                                        textAlign: i18n.language === 'ar' ? 'right' : 'left'
                                    }}>
                                        {i18n.language === 'ar' ? `استضافة: ${eventObj.host_name}` : `Hosted by: ${eventObj.host_name}`}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Main event details */}
                    {eventObj && (
                        <Box sx={{ p: 3, direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
                            {(eventObj?.start_datetime || (eventObj?.venue || eventObj?.address?.address)) && (
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    sx={{
                                        mb: 3,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        flexWrap: 'wrap',
                                        width: '100%',
                                        direction: 'ltr', // Keep LTR for Stack spacing, chips will have their own direction
                                    }}
                                >
                                    {eventObj?.start_datetime && (
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setModalOpen(true)}
                                                sx={{
                                                    borderRadius: 1,
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                                    py: 0.5,
                                                    px: 1.5,
                                                    minHeight: 32,
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem',
                                                    justifyContent: 'center',
                                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                                    color: 'black',
                                                    opacity: 0.9,
                                                    '& .widget-button-content': {
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: i18n.language === 'ar' ? '12px' : '8px',
                                                        position: 'relative',
                                                        [i18n.language === 'ar' ? 'right' : 'left']: '-8px'
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                        borderColor: 'rgba(0, 0, 0, 0.4)',
                                                        opacity: 1
                                                    }
                                                }}
                                            >
                                                <span className="widget-button-content">
                                                    <Calendar size={18} />
                                                    <span>{formatDate(eventObj?.start_datetime)}</span>
                                                </span>
                                            </Button>
                                        </Box>
                                    )}
                                    {(eventObj?.venue || eventObj?.address?.address) && (
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    const url = buildMapsUrl(
                                                        eventObj?.venue,
                                                        eventObj?.address?.address,
                                                        eventObj?.address?.lat,
                                                        eventObj?.address?.lng
                                                    );
                                                    if (url) {
                                                        window.open(url, '_blank');
                                                    }
                                                }}
                                                sx={{
                                                    borderRadius: 1,
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                                    py: 0.5,
                                                    px: 1.5,
                                                    minHeight: 32,
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem',
                                                    justifyContent: 'center',
                                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                                    color: 'black',
                                                    opacity: 0.9,
                                                    '& .widget-button-content': {
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: i18n.language === 'ar' ? '12px' : '8px',
                                                        position: 'relative',
                                                        [i18n.language === 'ar' ? 'right' : 'left']: '-8px'
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                        borderColor: 'rgba(0, 0, 0, 0.4)',
                                                        opacity: 1
                                                    }
                                                }}
                                            >
                                                <span className="widget-button-content">
                                                    <MapPin size={18} />
                                                    <span>{eventObj?.venue || eventObj?.address?.address || 'Location not available'}</span>
                                                </span>
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            )}

                            {/* Welcome Message */}
                            {/* {eventData?.data?.contact?.first_name && (
                            <Box sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 1,
                                    mb: 2,
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                                }}>
                                    <Typography variant="body1" color="text.secondary" sx={{
                                        textAlign: 'center'
                                    }}>
                                        {t('invitation.dear', 'Dear')} <span style={{ color: 'black' }}>{eventData.data.contact.first_name} {eventData.data.contact.last_name || ''}</span>{i18n.language === 'en' ? ', ' : ' '}{t('invitation.invitedToAttend', 'you are invited to attend')}
                                    </Typography>
                                </Box>
                            )} */}

                            {/* Current Status Display */}
                            {(eventData?.data?.status === 1 || eventData?.data?.status === 2 || true) && (


                                <Box sx={{
                                    p: 1.5, mb: 2,
                                    backgroundColor: eventData?.data?.status === 1 ? 'rgba(46, 125, 50, 0.08)' : eventData?.data?.status === 2 ? 'rgba(211, 47, 47, 0.08)' : 'rgba(211, 211, 211, 0.08)',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: eventData?.data?.status === 1 ? 'rgba(46, 125, 50, 0.2)' : eventData?.data?.status === 2 ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 211, 211, 0.2)',
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                                    }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color:
                                                    eventData?.data?.status === 1
                                                        ? 'success.dark'
                                                        : eventData?.data?.status === 2
                                                            ? 'error.dark'
                                                            : eventData?.data?.status === 3
                                                                ? 'warning.dark'
                                                                : 'text.secondary',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                textAlign: i18n.language === 'ar' ? 'right' : 'left',
                                            }}
                                        >
                                            {`${eventData?.data?.contact?.suffix || ''} ${eventData?.data?.contact?.first_name || ''} ${t('invitation.currentStatus', 'Current status')}`.trim()}:{' '}
                                            <Typography
                                                component="span"
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {
                                                    eventData?.data?.status === 1
                                                        ? t('invitation.going', 'GOING')
                                                        : eventData?.data?.status === 2
                                                            ? t('invitation.declined', 'DECLINED')
                                                            : eventData?.data?.status === 0 || eventData?.data?.status == null
                                                                ? t('invitation.noResponse', 'No Response')
                                                                : t('invitation.noResponse', 'No Response')
                                                }
                                            </Typography>
                                        </Typography>


                                        {eventData?.data?.status === 1 && (eventData?.data?.quantity_members || 0) > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'success.dark',
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem',
                                                        textAlign: i18n.language === 'ar' ? 'right' : 'left',
                                                    }}
                                                >
                                                    {t('invitation.guestsAttending', 'Guests attending')}: {eventData?.data?.victor || 0}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    onClick={() => setAdditionalGuestsModalOpen(true)}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        p: 0.5,
                                                        color: 'success.dark',
                                                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                                        '& .edit-button-content': {
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: i18n.language === 'ar' ? '8px' : '4px',
                                                            position: 'relative',
                                                            [i18n.language === 'ar' ? 'right' : 'left']: '-2px'
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(46, 125, 50, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <span className="edit-button-content">
                                                        <Edit size={14} color="green" />
                                                        <span>{t('buttons.edit')}</span>
                                                    </span>
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* QR Code (if attending) */}
                            {eventData?.data?.status === 1 && eventQrcode && !!eventObj?.invitation_show_qr_code && (
                                <Box sx={{
                                    p: 3, mb: 3, border: '1px dashed', borderColor: 'divider',
                                    borderRadius: 2, textAlign: 'center',
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                                }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        {t('invitation.eventPass', 'Entry Code')}
                                    </Typography>
                                    <Box
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {qrcodeLoading ? (
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {[0, 1, 2].map((index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            backgroundColor: '#000000',
                                                            animation: 'dotPulse 1.4s ease-in-out infinite',
                                                            animationDelay: `${index * 0.2}s`,
                                                            opacity: 0.3
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        ) : eventQrcode ? (
                                            <img
                                                src={qrBlobUrl || `data:image/png;base64,${eventQrcode}`}
                                                alt="Event QR Code"
                                                onClick={() => setQrPreviewOpen(true)}
                                                style={{
                                                    display: 'block',
                                                    maxWidth: 220,
                                                    maxHeight: 220,
                                                    width: 'auto',
                                                    height: 'auto',
                                                }}
                                            />
                                        ) : (
                                            <Typography color="text.secondary">{t('invitation.qrNotAvailable', 'QR code not available.')}</Typography>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        mt: 1,
                                        width: "100%",
                                        display: 'flex',
                                        justifyContent: "center",
                                        gap: 2, // Use gap instead of Stack spacing for better RTL support
                                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                        '& .qr-button-content': {
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: i18n.language === 'ar' ? '12px' : '8px', // More space for Arabic
                                            position: 'relative',
                                            [i18n.language === 'ar' ? 'right' : 'left']: '-8px'
                                        }
                                    }}>
                                        <Button
                                            onClick={() => setQrPreviewOpen(true)}
                                            sx={{
                                                fontWeight: 500,
                                                borderRadius: 2,
                                                px: 3,
                                                boxShadow: 'none',
                                                backgroundColor: 'black',
                                                color: 'white',
                                                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                                '&:hover': {
                                                    backgroundColor: '#222', // slightly lighter black for hover effect
                                                },
                                            }}
                                        >
                                            <span className="qr-button-content">
                                                <Eye size={18} color="white" />
                                                <span>{t('invitation.preview', 'Preview')}</span>
                                            </span>
                                        </Button>

                                        <Button
                                            onClick={handleDownload}
                                            sx={{
                                                fontWeight: 500,
                                                borderRadius: 2,
                                                px: 3,
                                                boxShadow: 'none',
                                                backgroundColor: 'black',
                                                color: 'white',
                                                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                                '&:hover': {
                                                    backgroundColor: '#222',
                                                },
                                            }}
                                        >
                                            <span className="qr-button-content">
                                                <DownloadIcon size={18} color="white" />
                                                <span>{t('invitation.download', 'Download')}</span>
                                            </span>
                                        </Button>

                                    </Box>
                                    <Modal
                                        open={qrPreviewOpen}
                                        onClose={() => setQrPreviewOpen(false)}
                                        aria-labelledby="qr-preview-modal"
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                bgcolor: 'background.paper',
                                                borderRadius: 3,
                                                boxShadow: 24,
                                                p: 4,
                                                outline: 'none',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                minWidth: { xs: 280, sm: 340 },
                                                maxWidth: '95vw',
                                                maxHeight: '90vh',
                                                overflowY: 'auto', // Make content scrollable if needed
                                            }}
                                        >
                                            <Typography id="qr-preview-modal" variant="h6" fontWeight="bold" gutterBottom>
                                                {t('invitation.qrCodePreview', 'QR Code Preview')}
                                            </Typography>
                                            {qrBlobUrl && (
                                                <img
                                                    src={qrBlobUrl}
                                                    alt="QR Code Preview"
                                                    style={{
                                                        display: 'block',
                                                        margin: '24px 0',
                                                        maxWidth: '100%',
                                                        maxHeight: '50vh', // Ensures image doesn't overflow modal
                                                        height: 'auto',
                                                    }}
                                                />
                                            )}
                                            <Box sx={{
                                                mt: 2,
                                                display: 'flex',
                                                gap: 2,
                                                direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                                            }}>
                                                <Button
                                                    sx={{ minWidth: 120 }}
                                                    onClick={() => setQrPreviewOpen(false)}
                                                    variant="contained"
                                                >
                                                    {t('invitation.close', 'Close')}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={handleDownload}
                                                    sx={{
                                                        minWidth: 120,
                                                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                                        '& .qr-button-content': {
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: i18n.language === 'ar' ? '12px' : '8px', // More space for Arabic
                                                            position: 'relative',
                                                            [i18n.language === 'ar' ? 'right' : 'left']: '-8px'
                                                        }
                                                    }}
                                                >
                                                    <span className="qr-button-content">
                                                        <DownloadIcon size={18} />
                                                        <span>{t('invitation.download', 'Download')}</span>
                                                    </span>
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Modal>
                                </Box>
                            )}

                            {/* Action buttons */}
                            <Box sx={{
                                display: 'flex', gap: 2, mb: 3,
                                flexDirection: { xs: 'column', sm: 'row' },
                                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                '& .button-content': {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    [i18n.language === 'ar' ? 'right' : 'left']: '-12px'
                                }
                            }}>
                                <Button
                                    variant={eventData?.data?.status === 1 ? 'contained' : 'outlined'}
                                    color={eventData?.data?.status === 1 ? 'inherit' : 'success'}
                                    size="large"
                                    onClick={handleGoingClick}
                                    disabled={actionLoading}
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        fontSize: 16,
                                        backgroundColor: eventData?.data?.status === 1 ? 'black' : 'transparent',
                                        color: eventData?.data?.status === 1 ? 'white' : 'success.main',
                                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                        '&:hover': {
                                            backgroundColor: eventData?.data?.status === 1 ? '#222' : undefined,
                                        },
                                    }}
                                >
                                    <span className="button-content">
                                        <CheckCircle size={20} color={eventData?.data?.status === 1 ? 'white' : 'green'} />
                                        <span>{eventData?.data?.status === 1 ? t('invitation.accepted', 'Accepted') : t('buttons.accept', 'Accept')}</span>
                                    </span>
                                </Button>

                                <Button
                                    variant={eventData?.data?.status === 2 ? 'contained' : 'outlined'}
                                    color={eventData?.data?.status === 2 ? 'inherit' : 'error'}
                                    size="large"
                                    onClick={() => setDeclineModalOpen(true)}
                                    disabled={actionLoading}
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        fontSize: 16,
                                        backgroundColor: eventData?.data?.status === 2 ? 'black' : 'transparent',
                                        color: eventData?.data?.status === 2 ? 'white' : 'red',
                                        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                        '&:hover': {
                                            backgroundColor: eventData?.data?.status === 2 ? '#222' : undefined,
                                        },
                                    }}
                                >
                                    <span className="button-content">
                                        <XCircle size={20} color={eventData?.data?.status === 2 ? 'white' : 'red'} />
                                        <span>{eventData?.data?.status === 2 ? t('invitation.declined', 'Declined') : t('buttons.decline', 'Decline')}</span>
                                    </span>
                                </Button>

                            </Box>

                            {guestPage?.downloadPrompt && justConfirmed && !!eventObj?.event_id && (
                                <GuestSlot>
                                    <GuestDownloadBox
                                        placement="after_confirm"
                                        config={guestPage}
                                        eventId={eventObj.event_id}
                                        language={guestLanguage(i18n.language)}
                                    />
                                </GuestSlot>
                            )}

                            {/* Message to Host Button */}
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                onClick={() => setMessageModalOpen(true)}
                                disabled={actionLoading}
                                fullWidth
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 'bold',
                                    fontSize: 16,
                                    backgroundColor: 'transparent',
                                    color: 'black',
                                    borderColor: 'black',
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        borderColor: 'black',
                                    },
                                    '& .button-content': {
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        position: 'relative',
                                        [i18n.language === 'ar' ? 'right' : 'left']: '-12px'
                                    }
                                }}
                            >
                                <span className="button-content">
                                    <MessageCircle size={20} color="black" />
                                    <span>{t('invitation.messageToHost', 'Message to Host')}</span>
                                </span>
                            </Button>

                            {actionError && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {actionError}
                                </Alert>
                            )}

                            {/* Event details sections */}
                            {eventObj?.description && (
                                <Box sx={{ mb: 3, direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                                        mb: 2,
                                        textAlign: i18n.language === 'ar' ? 'right' : 'left'
                                    }}>
                                        {t('invitation.eventDetails', 'Event Details')}
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{
                                        textAlign: i18n.language === 'ar' ? 'right' : 'left'
                                    }}>
                                        {eventObj.description}
                                    </Typography>
                                </Box>
                            )}

                            {/* Add to Wallet and Calendar buttons moved from location section */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                mb: 3,
                                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                '& .button-content': {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    [i18n.language === 'ar' ? 'right' : 'left']: '-12px'
                                }
                            }}>
                                {/* Add to Wallet Button */}
                                {eventObj?.allow_to_add_invitation_to_wallets === 1 && eventData?.data?.status === 1 && (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            backgroundColor: 'white',
                                            color: 'black',
                                            borderColor: 'black',
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: 16,
                                            py: 1.5,
                                            borderRadius: 2,
                                            direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                borderColor: 'black'
                                            },
                                        }}
                                        disabled={walletLoading}
                                        onClick={handleAddToWallet}
                                    >
                                        <span className="button-content">
                                            <Wallet size={18} color="black" />
                                            <span>{walletLoading ? t('invitation.loadingWalletPass', 'Loading Wallet Pass...') : t('invitation.addToWallet', 'Add to Wallet')}</span>
                                        </span>
                                    </Button>
                                )}

                                {walletError && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1, textAlign: 'center' }}>
                                        {walletError}
                                    </Typography>
                                )}

                            </Box>

                            <Divider sx={{ my: 3 }} />


                            <Divider sx={{ my: 3 }} />

                            {/* Additional info */}
                            {eventObj?.additional_info && (
                                <Box sx={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                                        mb: 2,
                                        textAlign: i18n.language === 'ar' ? 'right' : 'left'
                                    }}>
                                        {i18n.language === 'ar' ? 'معلومات إضافية' : 'Additional Information'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph sx={{
                                        textAlign: i18n.language === 'ar' ? 'right' : 'left'
                                    }}>
                                        {eventObj.additional_info}
                                    </Typography>
                                </Box>
                            )}

                            {guestPage?.downloadPrompt && !justConfirmed && !!eventObj?.event_id && (
                                <GuestSlot>
                                    <GuestDownloadBox
                                        placement="invitation"
                                        config={guestPage}
                                        eventId={eventObj.event_id}
                                        language={guestLanguage(i18n.language)}
                                    />
                                </GuestSlot>
                            )}

                            {guestPage?.partnerOffers && !!eventObj?.event_id && (
                                <GuestSlot>
                                    <GuestPartnerOffers config={guestPage} eventId={eventObj.event_id} language={guestLanguage(i18n.language)} />
                                </GuestSlot>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* Decline Confirmation Modal */}
                <Modal
                    open={declineModalOpen}
                    onClose={() => setDeclineModalOpen(false)}
                    aria-labelledby="decline-modal-title"
                >
                    <Box
                        sx={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            bgcolor: 'background.paper',
                            borderRadius: 2, boxShadow: 24, p: 4, outline: 'none',
                            direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                        }}
                    >
                        <Typography id="decline-modal-title" variant="h6" fontWeight="bold" gutterBottom>
                            {t('invitation.declineInvitation', 'Decline Invitation')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('invitation.whyDecline', "Let the host know why you can't attend (optional):")}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder={t('invitation.yourMessage', 'Your message...')}
                            value={declineMessage}
                            onChange={e => setDeclineMessage(e.target.value)}
                            sx={{ my: 2 }}
                            disabled={actionLoading}
                        />
                        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                            <Button
                                onClick={() => setDeclineModalOpen(false)}
                                color="inherit"
                                disabled={actionLoading}
                            >
                                {t('buttons.cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={async () => {
                                    await handleDecline(declineMessage);
                                    setDeclineModalOpen(false);
                                    setDeclineMessage('');
                                }}
                                disabled={actionLoading}
                                sx={{
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                    '& .button-content': {
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        position: 'relative',
                                        [i18n.language === 'ar' ? 'right' : 'left']: actionLoading ? '-12px' : '0'
                                    }
                                }}
                            >
                                {actionLoading ? (
                                    <span className="button-content">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>{t('invitation.confirmDecline', 'Confirm Decline')}</span>
                                    </span>
                                ) : (
                                    t('invitation.confirmDecline', 'Confirm Decline')
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Modal>

                {/* Going Confirmation Modal */}
                <Modal
                    open={goingModalOpen}
                    onClose={() => setGoingModalOpen(false)}
                    aria-labelledby="going-modal-title"
                >
                    <Box
                        sx={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            bgcolor: 'background.paper',
                            borderRadius: 2, boxShadow: 24, p: 4, outline: 'none',
                            direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                        }}
                    >
                        <Typography id="going-modal-title" variant="h6" fontWeight="bold" gutterBottom>
                            {t('invitation.numberOfAttendees', 'Number of Attendees')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('invitation.howManyAttending', 'How many people will be attending?')}
                        </Typography>

                        <Box sx={{ my: 3 }}>
                            <Button
                                fullWidth
                                variant={goingCount === 1 ? "contained" : "outlined"}
                                sx={{ mb: 1, py: 1.5 }}
                                onClick={() => {
                                    setGoingCount(1);
                                    handleResponse(1, 0);
                                    setGoingModalOpen(false);
                                }}
                                disabled={actionLoading}
                            >
                                {t('invitation.justMe', 'Just Me')}
                            </Button>
                            <Button
                                fullWidth
                                variant={goingCount === (eventData?.data.victor || 1) ? "contained" : "outlined"}
                                sx={{ mb: 2, py: 1.5 }}
                                onClick={() => {
                                    setGoingCount(eventData?.data.victor || 1);
                                    // Send only additional guests count (total - 1 main person)
                                    const additionalGuests = (eventData?.data.victor || 1) - 1;
                                    handleResponse(1, additionalGuests);
                                    setGoingModalOpen(false);
                                }}
                                disabled={actionLoading}
                            >
                                {t('invitation.allPeople', { count: eventData?.data.victor || 1, defaultValue: 'All ({{count}} People)' })}
                            </Button>

                            <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 1 }}>
                                {t('invitation.or', '— OR —')}
                            </Typography>

                            <TextField
                                type="number"
                                label={t('invitation.customNumber', 'Custom Number')}
                                fullWidth
                                value={goingCount === 0 ? '' : goingCount}
                                onChange={e => {
                                    const val = e.target.value;
                                    // Allow empty string for typing
                                    if (val === '') {
                                        setGoingCount(0);
                                    } else {
                                        setGoingCount(Number(val));
                                    }
                                }}
                                inputProps={{
                                    min: 1,
                                    max: eventData?.data.victor || 1,
                                }}
                                sx={{ mb: 2 }}
                                disabled={actionLoading}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            onClick={handleConfirmGuests}
                            sx={{
                                backgroundColor: 'success.main',
                                color: 'white',
                                borderRadius: 2,
                                '&:hover': { backgroundColor: 'success.dark' }
                            }}
                            fullWidth
                        >
                            {t('invitation.confirmAttendance', 'Confirm Attendance')}
                        </Button>

                        {guestCountError && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                {guestCountError}
                            </Typography>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            {t('invitation.updateNumberLater', 'You can update the number later if it changes.')}
                        </Typography>
                    </Box>
                </Modal>
                <CalendarModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    event={event}
                    eventhash={eventData?.data?.hash}
                />

                {/* Event Image Modal */}
                <Modal
                    open={eventImageModalOpen}
                    onClose={() => setEventImageModalOpen(false)}
                    aria-labelledby="event-image-modal"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 1, sm: 2 }
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            bgcolor: 'background.paper',
                            borderRadius: { xs: 2, sm: 3 },
                            boxShadow: 24,
                            outline: 'none',
                            maxWidth: { xs: '95vw', sm: '90vw', md: '80vw' },
                            maxHeight: { xs: '95vh', sm: '90vh' },
                            width: 'fit-content',
                            height: 'fit-content',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {eventObj?.pattern && (
                            <>
                                <img
                                    src={eventObj.pattern}
                                    alt="Event Banner"
                                    style={{
                                        display: 'block',
                                        maxWidth: '100%',
                                        maxHeight: '95vh',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        borderRadius: 'inherit'
                                    }}
                                    onError={(e) => {
                                        console.error('Image failed to load:', e);
                                        setEventImageModalOpen(false);
                                    }}
                                    onLoad={() => {
                                        console.log('Image loaded successfully');
                                    }}
                                />
                                {/* Close Button - Center bottom */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: { xs: 16, sm: 20 },
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: { xs: 36, sm: 48 },
                                        height: { xs: 36, sm: 48 },
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        zIndex: 1000,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                            transform: 'translateX(-50%) scale(1.1)',
                                        },
                                        '&:active': {
                                            transform: 'translateX(-50%) scale(0.95)',
                                        }
                                    }}
                                    onClick={() => setEventImageModalOpen(false)}
                                >
                                    <Typography
                                        sx={{
                                            color: 'white',
                                            fontSize: { xs: '20px', sm: '24px' },
                                            fontWeight: 'bold',
                                            lineHeight: 1,
                                            userSelect: 'none'
                                        }}
                                    >
                                        ×
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                </Modal>

                {/* Additional Guests Modal */}
                <Modal
                    open={additionalGuestsModalOpen}
                    onClose={() => setAdditionalGuestsModalOpen(false)}
                    aria-labelledby="additional-guests-modal"
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'background.paper',
                            borderRadius: 3,
                            boxShadow: 24,
                            p: 3,
                            outline: 'none',
                            maxWidth: 400,
                            width: '90%',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {t('invitation.howManyWithYou', 'How many people will attend with you?')}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('invitation.hostReserved', { count: totalSeatsReserved, guests: totalSeatsReserved - 1, defaultValue: 'The host has reserved {{count}} seats for you (you + {{guests}} guests). Please tell us the actual number attending.' })}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
                            <Button
                                variant={selectedGuestCount === 1 ? "contained" : "outlined"}
                                onClick={() => { setSelectedGuestCount(1); setCustomGuestCount(""); }}
                                sx={{
                                    minWidth: 100,
                                    borderRadius: 2,
                                    py: 1.2,
                                    backgroundColor: selectedGuestCount === 1 ? 'black' : 'transparent',
                                    color: selectedGuestCount === 1 ? 'white' : 'black',
                                    borderColor: 'black',
                                    '&:hover': {
                                        backgroundColor: selectedGuestCount === 1 ? '#222' : 'rgba(0, 0, 0, 0.04)',
                                        borderColor: 'black'
                                    }
                                }}
                            >
                                {t('invitation.justMe', 'Just Me')}
                            </Button>
                            <Button
                                variant={selectedGuestCount === totalSeatsReserved ? "contained" : "outlined"}
                                onClick={() => { setSelectedGuestCount(totalSeatsReserved); setCustomGuestCount(""); }}
                                sx={{
                                    minWidth: 100,
                                    borderRadius: 2,
                                    py: 1.2,
                                    backgroundColor: selectedGuestCount === totalSeatsReserved ? 'black' : 'transparent',
                                    color: selectedGuestCount === totalSeatsReserved ? 'white' : 'black',
                                    borderColor: 'black',
                                    '&:hover': {
                                        backgroundColor: selectedGuestCount === totalSeatsReserved ? '#222' : 'rgba(0, 0, 0, 0.04)',
                                        borderColor: 'black'
                                    }
                                }}
                            >
                                {t('invitation.allPeople', { count: totalSeatsReserved, defaultValue: 'All ({{count}} People)' })}
                            </Button>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('invitation.orSelectNumber', 'Or select number of attendees:')}
                        </Typography>

                        <TextField
                            select
                            value={customGuestCount}
                            onChange={(e) => { setCustomGuestCount(e.target.value); setSelectedGuestCount(null); }}
                            sx={{
                                width: '100%',
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': {
                                        borderColor: 'black',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'black',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'black',
                                    }
                                }
                            }}
                            size="small"
                        >
                            <MenuItem value="">{t('invitation.selectAttendanceCount', 'Select attendance count')}</MenuItem>
                            {[...Array(totalSeatsReserved)].map((_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                            ))}
                        </TextField>

                        <Button
                            variant="contained"
                            onClick={handleConfirmGuests}
                            sx={{
                                backgroundColor: 'success.main',
                                color: 'white',
                                borderRadius: 2,
                                '&:hover': { backgroundColor: 'success.dark' }
                            }}
                            fullWidth
                        >
                            {t('invitation.confirmAttendance', 'Confirm Attendance')}
                        </Button>

                        {guestCountError && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                {guestCountError}
                            </Typography>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            {t('invitation.updateNumberLater', 'You can update the number later if it changes.')}
                        </Typography>
                    </Box>
                </Modal>

                {/* Message to Host Modal */}
                <Modal
                    open={messageModalOpen}
                    onClose={() => {
                        setMessageModalOpen(false);
                        setMessageToHost('');
                    }}
                    aria-labelledby="message-modal-title"
                >
                    <Box
                        sx={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            bgcolor: 'background.paper',
                            borderRadius: 2, boxShadow: 24, p: 4, outline: 'none',
                            direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
                        }}
                    >
                        <Typography id="message-modal-title" variant="h6" fontWeight="bold" gutterBottom>
                            {t('invitation.messageToHost', 'Message to Host')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('invitation.messageToHostDescription', 'Send a message to the event host:')}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder={t('invitation.yourMessage', 'Your message...')}
                            value={messageToHost}
                            onChange={e => setMessageToHost(e.target.value)}
                            sx={{ my: 2 }}
                            disabled={actionLoading}
                        />
                        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                            <Button
                                onClick={() => {
                                    setMessageModalOpen(false);
                                    setMessageToHost('');
                                }}
                                color="inherit"
                                disabled={actionLoading}
                            >
                                {t('buttons.cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSendMessage}
                                disabled={actionLoading || !messageToHost.trim()}
                                sx={{
                                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                <MessageCircle size={16} color="white" />
                                <span>{t('buttons.send', 'Send')}</span>
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </Container>
        </>
    );
};

export default Invitation;